/**
 * Clerk authentication integration for managed mode (ALITA SaaS).
 *
 * In managed mode the login gate shows a Clerk sign-in widget instead of
 * the raw WebSocket-URL / token form. Once the user authenticates we fetch
 * their gateway connection info from the ALITA API and auto-connect.
 *
 * We load Clerk via its CDN script (the browser build includes UI components).
 * The ESM/npm build is headless and doesn't support mountSignIn.
 */

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const CLERK_PUBLISHABLE_KEY = "pk_live_Y2xlcmsuYWxpdGFncHQuY29tJA";
const API_BASE = "https://api.alitagpt.com";
const CLERK_FRONTEND_API = "https://clerk.alitagpt.com";
const CLERK_JS_URL = `${CLERK_FRONTEND_API}/npm/@clerk/clerk-js@latest/dist/clerk.browser.js`;

// ---------------------------------------------------------------------------
// Types — minimal Clerk interface for what we use
// ---------------------------------------------------------------------------

interface ClerkInstance {
  load: (opts?: Record<string, unknown>) => Promise<void>;
  user: unknown | null;
  session: { getToken: () => Promise<string | null> } | null;
  addListener: (cb: (resources: { user?: unknown; session?: { getToken: () => Promise<string | null> } | null }) => void) => (() => void);
  signOut: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Singleton Clerk instance
// ---------------------------------------------------------------------------

let clerkInstance: ClerkInstance | null = null;
let clerkReady = false;
let loadingPromise: Promise<ClerkInstance> | null = null;

const CLERK_LOAD_TIMEOUT_MS = 15_000;

function loadClerkScript(): Promise<void> {
  return Promise.race([
    new Promise<void>((resolve, reject) => {
      // Already loaded (script in index.html loads async)
      if ((window as Record<string, unknown>).Clerk) {
        resolve();
        return;
      }
      // Wait for the script tag in index.html to finish loading
      const existing = document.querySelector(`script[src*="clerk"]`);
      if (existing) {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () => reject(new Error("Failed to load sign-in. Please refresh.")));
        // Re-check after attaching listener — covers race where script loaded
        // between the initial check and addEventListener.
        if ((window as Record<string, unknown>).Clerk) {
          resolve();
        }
        return;
      }
      // Fallback: inject script if somehow missing from index.html
      const script = document.createElement("script");
      script.src = CLERK_JS_URL;
      script.async = true;
      script.crossOrigin = "anonymous";
      script.setAttribute("data-clerk-publishable-key", CLERK_PUBLISHABLE_KEY);
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load sign-in. Please refresh."));
      document.head.appendChild(script);
    }),
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error("Sign-in is taking too long. Please check your connection and refresh.")),
        CLERK_LOAD_TIMEOUT_MS,
      ),
    ),
  ]);
}

export async function getClerk(): Promise<ClerkInstance> {
  if (clerkInstance && clerkReady) return clerkInstance;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    await loadClerkScript();

    // The browser build sets window.Clerk after load, but we need to
    // instantiate it with our key if it's a constructor, or use the global.
    const win = window as Record<string, unknown>;
    const ClerkConstructor = win.Clerk as
      | (new (key: string) => ClerkInstance)
      | ClerkInstance
      | undefined;

    if (!ClerkConstructor) {
      throw new Error("Clerk failed to initialize — window.Clerk not found");
    }

    // If Clerk auto-initialized from the data attribute, it's already an instance
    if (typeof ClerkConstructor === "object" && "load" in ClerkConstructor) {
      clerkInstance = ClerkConstructor as ClerkInstance;
    } else if (typeof ClerkConstructor === "function") {
      clerkInstance = new (ClerkConstructor as new (key: string) => ClerkInstance)(
        CLERK_PUBLISHABLE_KEY,
      );
    } else {
      throw new Error("Something went wrong loading sign-in. Please refresh the page.");
    }

    // Skip load() if the index.html onload handler already called it.
    // Clerk sets session to null (not undefined) once loaded.
    const alreadyLoaded = (clerkInstance as unknown as Record<string, unknown>).loaded === true
      || clerkInstance.session !== undefined;
    if (!alreadyLoaded) {
      await clerkInstance!.load();
    }
    clerkReady = true;
    return clerkInstance!;
  })();

  return loadingPromise;
}

export function getClerkSync(): ClerkInstance | null {
  return clerkReady ? clerkInstance : null;
}

/**
 * Reset the Clerk singleton — must be called on sign-out to prevent
 * stale user data leaking between sessions.
 */
export function resetClerkState() {
  clerkInstance = null;
  clerkReady = false;
  loadingPromise = null;
}

// ---------------------------------------------------------------------------
// Authenticated API helper
// ---------------------------------------------------------------------------

/**
 * Wait for Clerk session to be available using addListener().
 * clerk.session is `undefined` while loading, `null` when no session.
 * On mobile / slow connections, session may not be hydrated when
 * clerk.load() resolves — addListener is the official way to wait.
 */
function waitForSession(clerk: ClerkInstance): Promise<{ getToken: () => Promise<string | null> }> {
  // Already available
  if (clerk.session) return Promise.resolve(clerk.session);

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error("Sign-in is taking too long. Please refresh the page and try again."));
    }, 30_000);

    const unsub = clerk.addListener(({ session }: { session?: { getToken: () => Promise<string | null> } | null }) => {
      if (session) {
        clearTimeout(timeout);
        unsub();
        resolve(session);
      }
    });
  });
}

/**
 * Make an authenticated fetch to the ALITA API using the current Clerk JWT.
 * Used by billing, provisioning, container-status, and connect-info calls.
 */
export async function apiFetchWithJWT(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const clerk = await getClerk();
  const session = await waitForSession(clerk);
  const jwt = await session.getToken();
  if (!jwt) throw new Error("Failed to obtain Clerk JWT");

  return fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
      ...(options.headers || {}),
    },
  });
}

// ---------------------------------------------------------------------------
// Gateway connect-info
// ---------------------------------------------------------------------------

export interface GatewayConnectInfo {
  subdomain: string;
  wsUrl: string;
  controlUiUrl: string;
  token: string;
}

export async function fetchConnectInfo(clerk: ClerkInstance): Promise<GatewayConnectInfo> {
  const res = await apiFetchWithJWT("/api/gateway/connect-info");

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`connect-info failed (${res.status}): ${body}`);
  }

  return res.json() as Promise<GatewayConnectInfo>;
}

// ---------------------------------------------------------------------------
// Clerk UI mount helpers
// ---------------------------------------------------------------------------
// Clerk is pre-loaded via <script> tag in index.html, so these are instant.

export function mountSignIn(el: HTMLElement): void {
  const clerk = getClerkSync();
  if (!clerk) return;
  (clerk as any).mountSignIn(el, {
    appearance: { variables: { colorPrimary: "#c0392b" } },
    signUpUrl: "/#sign-up",
    fallbackRedirectUrl: "/#sign-in",
    afterSignInUrl: "/#sign-in",
  });
}

export function mountSignUp(el: HTMLElement): void {
  const clerk = getClerkSync();
  if (!clerk) return;
  (clerk as any).mountSignUp(el, {
    appearance: { variables: { colorPrimary: "#c0392b" } },
    signInUrl: "/#sign-in",
    fallbackRedirectUrl: "/#sign-in",
    afterSignUpUrl: "/#sign-in",
  });
}

export function mountUserButton(el: HTMLElement): void {
  const clerk = getClerkSync();
  if (!clerk) return;
  (clerk as any).mountUserButton(el, {
    appearance: {
      variables: { colorPrimary: "#c0392b" },
      elements: { rootBox: { display: "flex" }, avatarBox: { width: "28px", height: "28px" } },
    },
  });
}

// ---------------------------------------------------------------------------
// Sign out
// ---------------------------------------------------------------------------

export async function signOut(clerk: ClerkInstance): Promise<void> {
  await clerk.signOut();
}
